import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import TermsDialog from '../termsBanner/TermsDialog.tsx';
import ChangelogDialog from '../changelog/ChangelogDialog.tsx';
import { useAuth } from '../auth/AuthContext';
import { getUserEmail } from '../../utils/oauthHelper';
import { ThemeToggle } from '../theme/ThemeToggle';
import { usePermissions } from '../../hooks/usePermissions';
import { useUserSubscription } from '../../contexts/UserSubscriptionContext';

import './Navbar.css';

interface NavbarProps {
  selectedTab: number;
  setSelectedTab: (tabIndex: number) => void;
  onLogout: () => void;
  isAuthenticated: boolean;
  onLogin: (response: any) => void;
  GOOGLE_CLIENT_ID: string;
}

const Navbar: React.FC<NavbarProps> = ({ selectedTab, setSelectedTab, onLogout, isAuthenticated, onLogin, GOOGLE_CLIENT_ID }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [changelogDialogOpen, setChangelogDialogOpen] = useState(false);
  const { hasFeature } = usePermissions();
  const hasAdminAccess = hasFeature('admin_view');
  const hasFamilyPlanAccess = hasFeature('family_plan_access');

  const userEmail = isAuthenticated ? (getUserEmail() || 'User') : 'User';

  const handleLogoutClick = () => {
    logout();
    onLogout();
  };

  const handleOpenTermsDialog = () => {
    setTermsDialogOpen(true);
  };

  const handleCloseTermsDialog = () => {
    setTermsDialogOpen(false);
  };

  const handleOpenChangelogDialog = () => {
    setChangelogDialogOpen(true);
  };

  const handleCloseChangelogDialog = () => {
    setChangelogDialogOpen(false);
  };

  return (
    <>
      <header className="relative border-b border-purple-400/30 shadow-lg shadow-purple-500/30 navbar-shimmer" style={{ 
        background: 'radial-gradient(ellipse at top, rgba(139, 66, 182, 0.9) 0%, rgba(106, 27, 154, 0.95) 50%, rgba(106, 27, 154, 1) 100%)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)'
      }}>
        {/* Realistic rainfall with ripple effect */}
        <div className="navbar-weather">
          {Array.from({ length: 15 }).map((_, i) => (
            <React.Fragment key={i}>
              <div className="rain"></div>
              <div className="ripple"></div>
            </React.Fragment>
          ))}
        </div>
        <div className="flex items-center justify-between px-4 py-3 w-full relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSelectedTab(0)}
            className="h-12 w-12 p-0 hover:bg-purple-400/20 flex-shrink-0"
          >
            <img 
              src="/naptune-logo-48px.png" 
              srcSet="/naptune-logo-48px.png 1x, /naptune-logo-96px.png 2x, /naptune-logo-144px.png 3x, /naptune-logo-192px.png 4x"
              alt="Naptune Logo"
              width="48"
              height="48"
              loading="eager"
              className="navbar-logo"
            />
          </Button>

          <h1
            onClick={() => setSelectedTab(0)}
            className="navbar-title absolute left-1/2 transform -translate-x-1/2 text-xl font-normal cursor-pointer text-white hover:text-purple-100 transition-colors pointer-events-auto drop-shadow-lg"
          >
            Naptune
          </h1>

          <div className="flex items-center gap-2 flex-shrink-0 ml-auto relative z-10">
            <ThemeToggle />
            {!isAuthenticated && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleOpenTermsDialog}
                className="h-9 w-9 text-white hover:bg-purple-400/20"
                aria-label="Terms and Conditions"
              >
                <ScrollText className="h-4 w-4" />
              </Button>
            )}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hover:bg-purple-400/20 text-white"
                  >
                    <span className="text-xl">☰</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">Hello, {userEmail}</p>
                    </div>
                  </DropdownMenuLabel>
                  
                  {/* Upload Management */}
                  <div className="relative">
                    <DropdownMenuSeparator />
                    <span className="absolute left-0.5 top-1/2 -translate-y-1/2 bg-popover px-1.5 text-[9px] text-muted-foreground/60 font-medium uppercase tracking-wider">
                      Uploads
                    </span>
                  </div>
                  <DropdownMenuItem onClick={() => { setSelectedTab(0); }} className="focus:text-primary pl-6">
                    <span>Tune Upload</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setSelectedTab(1); }} className="focus:text-primary pl-6">
                    <span>Upload History</span>
                  </DropdownMenuItem>
                  {hasFeature('upload_defaults') && (
                    <DropdownMenuItem onClick={() => { navigate('/upload-defaults'); }} className="focus:text-primary pl-6">
                      <span>Upload Defaults</span>
                    </DropdownMenuItem>
                  )}
                  
                  {/* Account & Billing */}
                  <div className="relative">
                    <DropdownMenuSeparator />
                    <span className="absolute left-0.5 top-1/2 -translate-y-1/2 bg-popover px-1.5 text-[9px] text-muted-foreground/60 font-medium uppercase tracking-wider">
                      Account
                    </span>
                  </div>
                  <DropdownMenuItem onClick={() => { navigate('/subscriptions'); }} className="focus:text-primary pl-6">
                    <span>Subscription</span>
                  </DropdownMenuItem>
                  {hasFamilyPlanAccess && (
                    <DropdownMenuItem onClick={() => { navigate('/family-plan'); }} className="focus:text-primary pl-6">
                      <span>Family Plan</span>
                    </DropdownMenuItem>
                  )}
                  
                  {/* Community */}
                  {hasFeature('leaderboard') && (
                    <>
                      <div className="relative">
                        <DropdownMenuSeparator />
                        <span className="absolute left-0.5 top-1/2 -translate-y-1/2 bg-popover px-1.5 text-[9px] text-muted-foreground/60 font-medium uppercase tracking-wider">
                          Community
                        </span>
                      </div>
                      <DropdownMenuItem onClick={() => { navigate('/leaderboard'); }} className="focus:text-primary pl-6">
                        <span>Leaderboard</span>
                      </DropdownMenuItem>
                    </>
                  )}
                  
                  {/* Admin */}
                  {hasAdminAccess && (
                    <>
                      <div className="relative">
                        <DropdownMenuSeparator />
                        <span className="absolute left-0.5 top-1/2 -translate-y-1/2 bg-popover px-1.5 text-[9px] text-muted-foreground/60 font-medium uppercase tracking-wider">
                          Admin
                        </span>
                      </div>
                      <DropdownMenuItem onClick={() => { navigate('/admin'); }} className="focus:text-primary pl-6">
                        <span>Admin Panel</span>
                      </DropdownMenuItem>
                    </>
                  )}
                  
                  {/* Information */}
                  <div className="relative">
                    <DropdownMenuSeparator />
                    <span className="absolute left-0.5 top-1/2 -translate-y-1/2 bg-popover px-1.5 text-[9px] text-muted-foreground/60 font-medium uppercase tracking-wider">
                      Help
                    </span>
                  </div>
                  <DropdownMenuItem onClick={handleOpenTermsDialog} className="focus:text-primary pl-6">
                    <span>Terms and Conditions</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleOpenChangelogDialog} className="focus:text-primary pl-6">
                    <span>What's New</span>
                  </DropdownMenuItem>
                  
                  {/* Logout */}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogoutClick} className="text-destructive focus:text-destructive">
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>

          <TermsDialog open={termsDialogOpen} onClose={handleCloseTermsDialog} />
          <ChangelogDialog open={changelogDialogOpen} onClose={handleCloseChangelogDialog} />
        </div>
      </header>
    </>
  );
};

export default Navbar;